import { memo, useContext, useEffect, useMemo, useState } from 'react';

import {
  useChronologicalAppointmentTypes,
  useChronologicalScheduleExpectations
} from '@/hooks/useScheduleExpectations';
import PersonContext from '@/pages/Person/PersonContext';
import { Alert, Select, Timeline } from '@/ui';

import ScheduleExpectationItemAccordion from './ScheduleExpectationItemAccordion';

type Props = {
  initialAppointmentTypeId?: string;
  initialExpectationId?: string;
  onAppointmentTypeChange?: (appointmentTypeId: string) => void;
};

const ScheduleExpectations = ({
  initialAppointmentTypeId = '',
  initialExpectationId = '',
  onAppointmentTypeChange
}: Props) => {
  const { person } = useContext(PersonContext);
  const phaseId = person.episode_of_care?.current_active_phase?.id || '';
  const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] =
    useState<string>(initialAppointmentTypeId);
  const { data: appointmentTypes, isPending: isPendingAppointmentTypes } =
    useChronologicalAppointmentTypes({ phaseId, enabled: !!phaseId });

  useEffect(() => {
    if (initialAppointmentTypeId) {
      setSelectedAppointmentTypeId(initialAppointmentTypeId);
    }
  }, [initialAppointmentTypeId]);

  useEffect(() => {
    if (appointmentTypes && appointmentTypes.length > 0 && !selectedAppointmentTypeId) {
      setSelectedAppointmentTypeId(appointmentTypes[0].id);
    }
  }, [appointmentTypes, selectedAppointmentTypeId]);

  useEffect(() => {
    onAppointmentTypeChange?.(selectedAppointmentTypeId);
  }, [selectedAppointmentTypeId, onAppointmentTypeChange]);

  const { data, isLoading, isError, error } = useChronologicalScheduleExpectations({
    phaseId,
    appointmentTypeId: selectedAppointmentTypeId,
    enabled: !!phaseId && !!selectedAppointmentTypeId
  });

  const activeObjectId = useMemo(() => {
    if (!initialExpectationId || !data) return null;
    const item = data.find(
      (d) =>
        d.object_id === initialExpectationId ||
        d.expectations.some(
          (e) =>
            e.custom_schedule_expectation_id === initialExpectationId ||
            e.schedule_expectation_id === initialExpectationId
        )
    );
    return item?.object_id ?? null;
  }, [data, initialExpectationId]);

  const appointmentTypeOptions = useMemo(
    () => (appointmentTypes ?? []).map((apt) => ({ label: apt.name, value: apt.id })),
    [appointmentTypes]
  );

  const selectedAppointmentTypeOption = useMemo(
    () => appointmentTypeOptions.find((o) => o.value === selectedAppointmentTypeId) ?? null,
    [appointmentTypeOptions, selectedAppointmentTypeId]
  );

  const hasAppointmentTypes = appointmentTypes && appointmentTypes.length > 0;

  const appointmentTypeSelect = hasAppointmentTypes ? (
    <div className="mb-4 max-w-80">
      <Select
        label="Appointment Type"
        options={appointmentTypeOptions}
        selectedOption={selectedAppointmentTypeOption}
        onChange={(opt) => {
          if (opt) setSelectedAppointmentTypeId(opt.value);
        }}
        clearable={false}
      />
    </div>
  ) : null;

  const renderContent = () => {
    if (!phaseId) {
      return (
        <Alert
          variant="info"
          message="This person has no active phase. Schedule expectations require an active phase."
        />
      );
    }

    if (isLoading || isPendingAppointmentTypes) {
      return <div>Loading...</div>;
    }

    if (isError) {
      return (
        <Alert variant="error" message={error?.message || 'Failed to load schedule expectations'} />
      );
    }

    if (!data || data.length === 0) {
      return <Alert variant="warning">No schedule expectations found</Alert>;
    }

    return (
      <Timeline>
        {data.map((item) => (
          <ScheduleExpectationItemAccordion
            key={`${item.object_id}-${item.object_id === activeObjectId}`}
            item={item}
            isActive={item.object_id === activeObjectId}
            activeExpectationId={item.object_id === activeObjectId ? initialExpectationId : null}
            phaseId={phaseId}
          />
        ))}
      </Timeline>
    );
  };

  return (
    <div>
      {appointmentTypeSelect}
      {renderContent()}
    </div>
  );
};

export default memo(ScheduleExpectations);
